import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import SEO from "../components/SEO";

const BlogPost = () => {
  const { id } = useParams();

  const posts = {
    "how-to-qualify-for-business-funding": {
      title: "How to Qualify for Business Funding in 2026",
      date: "January 28, 2026",
      readTime: "5 min read",
      category: "Funding Tips",
      content: [
        {
          type: "paragraph",
          text: "Getting approved for business funding doesn't have to be complicated. Whether you're looking for a line of credit, term loan, or merchant cash advance, lenders typically look for a few key factors when evaluating your application.",
        },
        {
          type: "heading",
          text: "1. Time in Business",
        },
        {
          type: "paragraph",
          text: "Most lenders prefer businesses that have been operating for at least 6 months to a year. This shows stability and gives lenders confidence that your business can handle repayment. If you're a newer business, don't worry — some funding options like merchant cash advances have more flexible requirements.",
        },
        {
          type: "heading",
          text: "2. Monthly Revenue",
        },
        {
          type: "paragraph",
          text: "Your revenue is one of the most important factors. Lenders want to see consistent income that demonstrates your ability to repay. Generally, businesses with $10,000 or more in monthly revenue have more options available. The higher your revenue, the more funding you may qualify for.",
        },
        {
          type: "heading",
          text: "3. Credit Score",
        },
        {
          type: "paragraph",
          text: "While traditional bank loans often require excellent credit, alternative lenders are more flexible. Many of our funding options work with credit scores as low as 500. We look at the whole picture, not just your credit score.",
        },
        {
          type: "heading",
          text: "4. Bank Statements",
        },
        {
          type: "paragraph",
          text: "Be prepared to provide 3-4 months of business bank statements. These help lenders verify your revenue and understand your cash flow patterns. Consistent deposits and a healthy average balance work in your favor.",
        },
        {
          type: "heading",
          text: "5. Business Documentation",
        },
        {
          type: "paragraph",
          text: "Having your business documents in order speeds up the process. This includes your EIN, business license, and proof of ownership. The good news? Most alternative lenders require far less paperwork than traditional banks.",
        },
        {
          type: "heading",
          text: "Tips to Improve Your Chances",
        },
        {
          type: "paragraph",
          text: "Before applying, consider these steps: Review your bank statements for any red flags like overdrafts or negative balances. Make sure your business information is consistent across all documents. Be honest on your application — discrepancies can delay approval or lead to denial.",
        },
        {
          type: "paragraph",
          text: "At Lloyd Funding, we work with businesses at all stages. Even if you've been turned down elsewhere, we may have options for you. Our team reviews each application individually to find the best funding solution for your situation.",
        },
      ],
    },
    "mca-vs-term-loan": {
      title: "Merchant Cash Advance vs. Term Loan: Which Is Right for You?",
      date: "January 20, 2026",
      readTime: "4 min read",
      category: "Funding Options",
      content: [
        {
          type: "paragraph",
          text: "When it comes to business funding, two popular options are merchant cash advances (MCAs) and term loans. Both can provide the capital you need, but they work very differently. Understanding these differences can help you choose the right option for your business.",
        },
        {
          type: "heading",
          text: "What Is a Merchant Cash Advance?",
        },
        {
          type: "paragraph",
          text: "A merchant cash advance provides a lump sum of capital in exchange for a percentage of your future sales. Instead of fixed monthly payments, you repay through automatic daily or weekly deductions based on your revenue. When sales are high, you pay the same. When sales are slow, payments can be adjusted.",
        },
        {
          type: "heading",
          text: "What Is a Term Loan?",
        },
        {
          type: "paragraph",
          text: "A term loan is a traditional financing option where you receive a lump sum and repay it over a set period with fixed payments. The terms typically range from 1 to 10 years, and you'll know exactly what your payment will be each month.",
        },
        {
          type: "heading",
          text: "Key Differences",
        },
        {
          type: "paragraph",
          text: "Repayment structure is the biggest difference. MCAs flex with your revenue, making them ideal for businesses with seasonal fluctuations. Term loans offer predictability with fixed payments, which is great for budgeting.",
        },
        {
          type: "paragraph",
          text: "Approval requirements also differ. MCAs typically have easier qualification requirements and faster approval — sometimes same-day funding. Term loans may require better credit and more documentation but often offer lower overall costs.",
        },
        {
          type: "heading",
          text: "When to Choose an MCA",
        },
        {
          type: "paragraph",
          text: "Consider an MCA if: You have fluctuating or seasonal revenue. You need funding quickly. Your credit score isn't perfect. You prefer flexible payments that adjust to your cash flow.",
        },
        {
          type: "heading",
          text: "When to Choose a Term Loan",
        },
        {
          type: "paragraph",
          text: "Consider a term loan if: You have strong, consistent revenue. You prefer predictable monthly payments. You're making a long-term investment like equipment or expansion. You have good credit and can qualify for better rates.",
        },
        {
          type: "paragraph",
          text: "Not sure which option is right for you? Our funding specialists can review your situation and recommend the best solution. We offer both MCAs and term loans, so we'll match you with the option that fits your business needs.",
        },
      ],
    },
    "improve-business-cash-flow": {
      title: "5 Ways to Improve Your Business Cash Flow",
      date: "January 15, 2026",
      readTime: "6 min read",
      category: "Business Tips",
      content: [
        {
          type: "paragraph",
          text: "Cash flow is the lifeblood of any business. Even profitable companies can struggle if money isn't coming in fast enough to cover expenses. Here are five practical strategies to improve your business cash flow.",
        },
        {
          type: "heading",
          text: "1. Invoice Promptly and Follow Up",
        },
        {
          type: "paragraph",
          text: "The sooner you invoice, the sooner you get paid. Send invoices immediately after delivering products or services. Set up automatic payment reminders for overdue accounts. Consider offering small discounts for early payment to incentivize faster collection.",
        },
        {
          type: "heading",
          text: "2. Review Your Payment Terms",
        },
        {
          type: "paragraph",
          text: "If you're offering Net 60 or Net 90 terms, consider tightening them. Net 30 is standard for most industries. For new customers, you might require deposits or payment upon delivery until they establish a payment history with you.",
        },
        {
          type: "heading",
          text: "3. Manage Inventory Wisely",
        },
        {
          type: "paragraph",
          text: "Money tied up in excess inventory is money that's not working for you. Analyze your sales data to identify slow-moving items. Consider just-in-time ordering for products with reliable suppliers. Liquidate obsolete inventory, even at a discount, to free up cash.",
        },
        {
          type: "heading",
          text: "4. Negotiate with Suppliers",
        },
        {
          type: "paragraph",
          text: "Don't be afraid to negotiate payment terms with your suppliers. Many will offer extended terms or discounts for loyal customers. Even an extra 15 days to pay can make a significant difference in your cash flow cycle.",
        },
        {
          type: "heading",
          text: "5. Establish a Line of Credit",
        },
        {
          type: "paragraph",
          text: "A business line of credit provides a safety net for cash flow gaps. Unlike a loan, you only pay interest on what you use. Having a credit line in place before you need it means you're prepared for unexpected expenses or opportunities.",
        },
        {
          type: "paragraph",
          text: "Implementing even one or two of these strategies can significantly improve your cash flow. And if you need additional working capital to bridge gaps or fuel growth, Lloyd Funding offers flexible solutions designed to keep your business moving forward.",
        },
      ],
    },
    "when-to-seek-business-funding": {
      title: "When Is the Right Time to Seek Business Funding?",
      date: "January 8, 2026",
      readTime: "4 min read",
      category: "Funding Tips",
      content: [
        {
          type: "paragraph",
          text: "Timing is everything when it comes to business funding. Apply too early, and you might not qualify. Wait too long, and you could miss opportunities or find yourself in a cash crunch. Here's how to know when the time is right.",
        },
        {
          type: "heading",
          text: "You're Ready to Expand",
        },
        {
          type: "paragraph",
          text: "Growth opportunities don't wait. Whether it's opening a new location, hiring staff, or expanding your product line, having capital ready allows you to move quickly. If your business is consistently profitable and you see clear expansion opportunities, it might be time to explore funding options.",
        },
        {
          type: "heading",
          text: "You Need to Purchase Equipment",
        },
        {
          type: "paragraph",
          text: "Equipment purchases often require significant upfront capital. Rather than depleting your cash reserves, financing allows you to spread the cost over time while immediately benefiting from the equipment. This is especially smart when the equipment will generate additional revenue.",
        },
        {
          type: "heading",
          text: "You're Experiencing Seasonal Fluctuations",
        },
        {
          type: "paragraph",
          text: "Many businesses experience predictable slow periods. A line of credit or short-term funding can help you maintain operations during off-seasons and stock up for busy periods. The key is to plan ahead — secure funding before you desperately need it.",
        },
        {
          type: "heading",
          text: "You Have a Large Order or Contract",
        },
        {
          type: "paragraph",
          text: "Landing a big contract is exciting, but fulfilling it requires capital. You may need to purchase inventory, hire temporary staff, or cover other costs before you receive payment. Short-term funding can help you take advantage of these opportunities without overextending.",
        },
        {
          type: "heading",
          text: "You Want to Consolidate Debt",
        },
        {
          type: "paragraph",
          text: "If you're juggling multiple loans or high-interest debt, consolidation can simplify your payments and potentially reduce your overall costs. This frees up cash flow and makes financial management easier.",
        },
        {
          type: "heading",
          text: "Warning Signs You've Waited Too Long",
        },
        {
          type: "paragraph",
          text: "Don't wait until you're in crisis mode. If you're regularly missing payments, your credit is declining, or you're unable to make payroll, you've waited too long. While we can often still help, your options may be more limited and costs higher.",
        },
        {
          type: "paragraph",
          text: "The best time to explore funding is when your business is healthy and you can qualify for favorable terms. At Lloyd Funding, we help businesses at all stages find the right funding solution. Reach out today to discuss your options — even if you're not ready to apply yet.",
        },
      ],
    },
  };

  const post = posts[id];

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <>
      <SEO
        title={post.title}
        description={post.content[0]?.text || ""}
        canonical={`/blog/${id}`}
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-blue-50 to-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="mx-auto max-w-3xl">
            <Link
              to="/blog"
              className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 transition"
            >
              <svg
                className="mr-2 h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                />
              </svg>
              Back to Blog
            </Link>

            <div className="mt-6 flex items-center gap-3">
              <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">
                {post.category}
              </span>
              <span className="text-sm text-slate-500">{post.readTime}</span>
            </div>

            <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-800 sm:text-4xl">
              {post.title}
            </h1>

            <p className="mt-4 text-slate-500">{post.date}</p>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <article className="prose prose-slate prose-lg max-w-none">
            {post.content.map((block, index) => {
              if (block.type === "heading") {
                return (
                  <h2
                    key={index}
                    className="mt-8 text-xl font-bold text-slate-800"
                  >
                    {block.text}
                  </h2>
                );
              }
              return (
                <p key={index} className="mt-4 text-slate-600 leading-relaxed">
                  {block.text}
                </p>
              );
            })}
          </article>

          {/* CTA */}
          <div className="mt-12 rounded-2xl bg-blue-50 p-6 sm:p-8">
            <h3 className="text-xl font-bold text-slate-800">
              Ready to get started?
            </h3>
            <p className="mt-2 text-slate-600">
              Apply today and get funded as soon as the same day.
            </p>
            <Link
              to="/contact#application-form"
              className="mt-4 inline-flex items-center justify-center rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 transition"
            >
              Apply Now
            </Link>
          </div>
        </div>
      </section>

      {/* More Articles */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <h2 className="text-2xl font-bold text-slate-800">More Articles</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(posts)
              .filter(([key]) => key !== id)
              .slice(0, 3)
              .map(([key, otherPost]) => (
                <Link
                  key={key}
                  to={`/blog/${key}`}
                  className="group rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-shadow duration-200 hover:shadow-lg hover:ring-1 hover:ring-blue-100"
                >
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                    {otherPost.category}
                  </span>
                  <h3 className="mt-3 text-lg font-bold text-slate-800 group-hover:text-blue-700 transition">
                    {otherPost.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-500">
                    {otherPost.date}
                  </p>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogPost;
